/* eslint-disable react/jsx-indent, react/jsx-indent-props */
/* eslint-disable react/jsx-closing-bracket-location, react/no-danger */
import React, { useId } from "react";

type ContainerFrameProps = {
  width?: number | string;
  height?: number | string;
  className?: string;
  imageSrc?: string | null;
  alt?: string;
  /**
   * How the inner image should scale inside the circular area.
   * Defaults to 'xMidYMid slice' (cover behavior).
   */
  preserveAspectRatio?: string;
  /**
   * 0-100 percentage to render as progress on the outer ring.
   * 0 means no progress, 100 means full ring.
   */
  progress?: number;
  /** Solid color for the outer progress ring stroke. */
  outerRingColor?: string;
  /** Stroke width for the outer progress ring. */
  outerRingStrokeWidth?: number;

  /** Inner ring stroke color. */
  innerRingColor?: string;
  /** Inner ring stroke width. */
  innerRingStrokeWidth?: number;
  /** Badge logo to display in the bottom-right corner */
  badgeLogo?: string | null;
  /** Initials or fallback text shown when imageSrc is absent */
  label?: string;
};

/**
 * Reusable SVG frame component based on Container.svg that allows a dynamic inner image.
 * The circular area is clipped to a circle (cx=56, cy=55.6428, r=50) per the original SVG.
 */
export function AchievementAvatar({
  width = 112,
  height = 112,
  className,
  imageSrc,
  alt,
  preserveAspectRatio = "xMidYMid slice",
  progress = 100,
  outerRingColor = "#935B24",
  outerRingStrokeWidth = 3,
  innerRingColor = "transparent",
  innerRingStrokeWidth = 2,
  badgeLogo,
  label,
}: ContainerFrameProps) {
  const uniqueId = useId().replace(/:/g, "_");
  const maskId = `container_frame_mask_${uniqueId}`;
  const ringClipId = `container_frame_ring_clip_${uniqueId}`;
  const innerCircleClipId = `container_frame_inner_circle_clip_${uniqueId}`;
  const cornerClipId = `container_frame_corner_clip_${uniqueId}`;
  const badgeHexagonClipId = `badge_hexagon_clip_${uniqueId}`;
  // Progress ring geometry similar to CircularProgressBadge
  const centerX = 56;
  const centerY = 55.6428;
  const progressRadius = 53; // fits the existing ring around the inner image (r=50)
  const innerRingRadius = Math.max(0, 50 - innerRingStrokeWidth / 2); // just inside the image edge
  const circumference = 2 * Math.PI * progressRadius;
  const clampedProgress = Math.max(0, Math.min(100, progress));
  const dashOffset = circumference * (1 - clampedProgress / 100);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width={width}
      height={height}
      viewBox="0 0 116 112"
      fill="none"
      className={className}
      role={alt ? "img" : "presentation"}
      aria-label={alt}
    >
      {alt ? <title>{alt}</title> : null}
      <defs>
        {/* Original outer mask from Container.svg (ids made unique) */}
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x={0}
          y={-0.357178}
          width={112}
          height={112}
          fill="black"
        >
          <rect fill="white" y={-0.357178} width={112} height={112} />
          <path d="M4 55.6428C4 26.924 27.2812 3.64282 56 3.64282C84.7188 3.64282 108 26.924 108 55.6428C108 84.3616 84.7188 107.643 56 107.643C27.2812 107.643 4 84.3616 4 55.6428Z" />
        </mask>
        {/* ClipPath describing the ring shape, referencing the mask (from original) */}
        <clipPath id={ringClipId}>
          <path
            d="M56 107.643V104C29.2931 104 7.64286 82.3497 7.64286 55.6428H4H0.357143C0.357143 86.3735 25.2693 111.286 56 111.286V107.643ZM108 55.6428H104.357C104.357 82.3497 82.7069 104 56 104V107.643V111.286C86.7307 111.286 111.643 86.3735 111.643 55.6428H108ZM56 3.64282V7.28568C82.7069 7.28568 104.357 28.9359 104.357 55.6428H108H111.643C111.643 24.9121 86.7307 -3.48091e-05 56 -3.48091e-05V3.64282ZM56 3.64282V-3.48091e-05C25.2693 -3.48091e-05 0.357143 24.9121 0.357143 55.6428H4H7.64286C7.64286 28.9359 29.2931 7.28568 56 7.28568V3.64282Z"
            mask={`url(#${maskId})`}
          />
        </clipPath>
        {/* ClipPath for the inner circular image area */}
        <clipPath id={innerCircleClipId}>
          <circle cx={56} cy={55.6428} r={50} />
        </clipPath>
        {/* ClipPath for the bottom-right corner icon area */}
        <clipPath id={cornerClipId}>
          <rect
            width="26.1841"
            height="34.6667"
            fill="white"
            transform="translate(84.908 76.3096)"
          />
        </clipPath>
        {/* ClipPath for badge logo in hexagonal shape */}
        <clipPath id={badgeHexagonClipId}>
          <path d="M107.634 85.3303V96.6516L97.907 102.308L88.1824 96.6506V85.3293L97.907 79.6741L107.634 85.3303Z" />
        </clipPath>
      </defs>

      {/* Outer progress ring (0..100%), solid color - only show if color is not transparent */}
      {outerRingColor !== "transparent" && (
        <circle
          cx={centerX}
          cy={centerY}
          r={progressRadius}
          stroke={outerRingColor}
          strokeWidth={outerRingStrokeWidth}
          strokeLinecap="butt"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${centerX} ${centerY})`}
          clipPath={`url(#${ringClipId})`}
        />
      )}
      {/* Dynamic inner image clipped to the circular area */}
      {imageSrc ? (
        <image
          href={imageSrc}
          x={6}
          y={5.6428}
          width={100}
          height={100}
          clipPath={`url(#${innerCircleClipId})`}
          preserveAspectRatio={preserveAspectRatio}
        />
      ) : label ? (
        <>
          <circle
            cx={centerX}
            cy={centerY}
            r={50}
            fill="#E5E7EB"
            clipPath={`url(#${innerCircleClipId})`}
          />
          <text
            x={centerX}
            y={centerY}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={28}
            fontWeight="600"
            fill="#6B7280"
            clipPath={`url(#${innerCircleClipId})`}
          >
            {label}
          </text>
        </>
      ) : null}
      {/* Inner ring (transparent fill, simple stroke) - render above image */}
      <circle
        cx={centerX}
        cy={centerY}
        r={innerRingRadius}
        stroke={innerRingColor}
        strokeWidth={innerRingStrokeWidth}
        fill="none"
      />
      {/* Bottom-right corner badge logo - only show if badgeLogo is provided */}
      {badgeLogo && (
        <g clipPath={`url(#${cornerClipId})`}>
          <g>
            {/* White border for badge logo */}
            <path
              d="M107.634 85.3303V96.6516L97.907 102.308L88.1824 96.6506V85.3293L97.907 79.6741L107.634 85.3303Z"
              fill="none"
              stroke="white"
              strokeWidth="2.5"
              strokeLinejoin="round"
            />
            {/* Badge logo image */}
            <g clipPath={`url(#${badgeHexagonClipId})`}>
              <image
                href={badgeLogo}
                x={86.5}
                y={78}
                width={23}
                height={27}
                preserveAspectRatio="xMidYMid slice"
              />
            </g>
          </g>
        </g>
      )}
    </svg>
  );
}

export default AchievementAvatar;
